import { computed, ref, watch, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { getClientById, type ClientAccountStatus } from "@/services/clientService";
import type { NormalizedClient } from "@/composables/useClientsQuery";

type ClientId = NormalizedClient["id"];

export interface RenewalHistoryEntry {
  id?: string | number;
  action?: string | null;
  status_from?: string | null;
  status_to?: string | null;
  effective_date?: string | null;
  actor_type?: string | null;
  actor_id?: string | number | null;
  actor_name?: string | null;
  note?: string | null;
  previous_values?: Record<string, unknown> | null;
  new_values?: Record<string, unknown> | null;
  created_at?: string | null;
}

export interface RenewalTrackingDetail {
  client_account_status?: ClientAccountStatus | null;
  renewal_note?: string | null;
  renewal_history?: RenewalHistoryEntry[];
}

export function useRenewalTracking(clients: Ref<NormalizedClient[]>) {
  const selectedClientId = ref<ClientId | null>(null);

  const selectedClient = computed(
    () =>
      clients.value.find(
        (client) => String(client.id) === String(selectedClientId.value)
      ) ?? null
  );

  const selectedClientReference = computed(() => {
    const client = selectedClient.value;
    return client?.raw?.route_key || client?.id || null;
  });

  watch(
    clients,
    (nextClients) => {
      const selectedStillVisible = nextClients.some(
        (client) => String(client.id) === String(selectedClientId.value)
      );

      if (!selectedStillVisible) {
        selectedClientId.value = nextClients[0]?.id ?? null;
      }
    },
    { immediate: true }
  );

  const detailQuery = useQuery({
    queryKey: computed(() => [
      "renewal-tracking-client",
      selectedClientReference.value == null
        ? ""
        : String(selectedClientReference.value),
    ]),
    queryFn: async () => {
      if (selectedClientReference.value == null) return null;
      return (await getClientById(
        selectedClientReference.value
      )) as RenewalTrackingDetail | null;
    },
    enabled: computed(() => selectedClientReference.value != null),
    staleTime: 30_000,
  });

  const selectClient = (clientId: ClientId) => {
    selectedClientId.value = clientId;
  };

  return {
    selectedClientId,
    selectedClient,
    selectedClientDetail: detailQuery.data,
    detailIsLoading: detailQuery.isLoading,
    detailIsError: detailQuery.isError,
    refetchDetails: detailQuery.refetch,
    selectClient,
  };
}
